/**
 * 픽셀 스프라이트를 코드로 그려서 아틀라스 한 장에 굽는다.
 *
 * 아트 에셋이 아직 없으니 도트를 문자열로 찍는다. 나중에 실제 아트가 들어오면
 * 이 파일만 갈아끼우면 된다. 렌더러는 아틀라스에서 이름으로 텍스처를 꺼내 쓸 뿐이다.
 *
 * 전부 한 장에 모으는 이유는 드로우콜 때문이다. 텍스처가 같으면 픽시가 수백 개
 * 스프라이트를 한 번에 묶어 보낸다.
 */
import { Rectangle, Texture } from 'pixi.js';
import { BranchCode, Culture, TROOPS } from '../sim/troops.ts';

const CELL = 28;
const COLS = 8;

/** 병종 데이터의 문자열 가지를 숫자 코드로. */
const BRANCH_CODE: Record<string, number> = {
  levy: BranchCode.Levy,
  spear: BranchCode.Spear,
  blade: BranchCode.Blade,
  bow: BranchCode.Bow,
  horse: BranchCode.Horse,
};

/** 진영별 색. 고구려는 청동과 철, 백제는 자주, 신라는 붉은 흙. */
interface Palette {
  body: string;
  trim: string;
  metal: string;
  dark: string;
  skin: string;
  wood: string;
}

const CULTURE_PALETTE: Record<number, Palette> = {
  [Culture.Goguryeo]: {
    body: '#3c5a6e',
    trim: '#8fb8c8',
    metal: '#b9c4cc',
    dark: '#16202a',
    skin: '#e2b48c',
    wood: '#7a5230',
  },
  [Culture.Baekje]: {
    body: '#5b3f6e',
    trim: '#b294c8',
    metal: '#c2bcc8',
    dark: '#1e1826',
    skin: '#e2b48c',
    wood: '#7a5230',
  },
  [Culture.Silla]: {
    body: '#7d4038',
    trim: '#d8a878',
    metal: '#a8907a',
    dark: '#241412',
    skin: '#e2b48c',
    wood: '#7a5230',
  },
};

/** 보병 몸통. 위에서 비스듬히 내려다본 12x16. */
const FOOT = [
  '....dddd....',
  '...dmmmmd...',
  '..dmmmmmmd..',
  '..dmmmmmmd..',
  '...dssssd...',
  '...dsddsd...',
  '....dssd....',
  '..dbbbbbbd..',
  '.dbbbbbbbbd.',
  '.dbtttttbd..',
  '.dsbbbbbbsd.',
  '..dbbbbbbd..',
  '...dbbbbd...',
  '...dd..dd...',
  '...dd..dd...',
  '...d....d...',
];

/** 말. 20x18. 기수는 이 위에 따로 얹는다. */
const HORSE = [
  '..........dddd......',
  '.........dmmmmd.....',
  '........dmmmmmd.....',
  '.......dmmmmmd......',
  '..ddddddmmmmd.......',
  '.dhhhhhhhhhd........',
  'dhhhhhhhhhhhd.......',
  'dhhhhhhhhhhhhd......',
  'dhhhhhhhhhhhhd......',
  '.dhhhhhhhhhhd.......',
  '..dhhhhhhhhd........',
  '..dh.dhhd.hd........',
  '..dd.dhhd.dd........',
  '..d..dhhd..d........',
  '.....dd.dd..........',
  '.....dd.dd..........',
  '.....d...d..........',
  '....................',
];

function put(
  ctx: CanvasRenderingContext2D,
  rows: readonly string[],
  map: Record<string, string>,
  ox: number,
  oy: number,
): void {
  for (let y = 0; y < rows.length; y++) {
    const row = rows[y];
    for (let x = 0; x < row.length; x++) {
      const color = map[row[x]];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(ox + x, oy + y, 1, 1);
    }
  }
}

function rect(ctx: CanvasRenderingContext2D, c: string, x: number, y: number, w: number, h: number): void {
  ctx.fillStyle = c;
  ctx.fillRect(x, y, w, h);
}

function footMap(p: Palette, gold: boolean): Record<string, string> {
  return {
    d: p.dark,
    m: gold ? '#d9a33a' : p.metal,
    s: p.skin,
    b: p.body,
    t: gold ? '#e8c96a' : p.trim,
  };
}

function horseMap(p: Palette): Record<string, string> {
  return { d: p.dark, h: '#4a4a52', m: '#3a3a42' };
}

export interface AtlasResult {
  readonly texture: Texture;
  readonly frames: ReadonlyMap<string, Texture>;
  readonly canvas: HTMLCanvasElement;
}

/**
 * 스프라이트 키는 문화와 가지와 티어로만 정한다.
 * 아군인지 적군인지는 발밑 고리가 말해주므로 같은 그림을 양쪽이 나눠 쓴다.
 * 팀까지 키에 넣으면 그림 수가 두 배가 되는데 얻는 게 없다.
 */
export function troopSpriteKey(culture: number, branch: number, tier: number): string {
  return `t${culture}_${branch}_${tier}`;
}

export const HERO_LEADER_KEY = 'hero_leader';
/** 말이 없는 장수. 같은 색에 칼을 든 보병 몸이다. */
export const HERO_LEADER_FOOT_KEY = 'hero_leader_foot';
export const HERO_CHIEF_KEY = 'hero_chief';
/** 말에서 내린 적장(산길). 시트를 못 쓸 때의 대체 그림이다. */
export const HERO_CHIEF_FOOT_KEY = 'hero_chief_foot';

/**
 * 분대 색. 난전에서 어느 깃발이 누구 것인지 한눈에 읽혀야 한다.
 * 0번은 장수 직할대라 금색이고, 동료 분대는 서로 확실히 다른 색을 쓴다.
 */
export const SQUAD_COLORS: readonly string[] = ['#e8c96a', '#5fc8d8', '#7fc86a', '#c07fd8'];

export function bannerKey(squad: number): string {
  return `banner${squad}`;
}

export function companionKey(squad: number): string {
  return `comp${squad}`;
}

/** 말이 없는 동료. 걷는 장수처럼 같은 색에 칼을 든 보병 몸이다. */
export function companionFootKey(squad: number): string {
  return `comp${squad}_foot`;
}

/** 색을 밝히거나 어둡게. 티어를 갑옷 밝기로 보여주는 데 쓴다. */
function shade(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const out = amount >= 0 ? v + (255 - v) * amount : v * (1 + amount);
    return Math.max(0, Math.min(255, Math.round(out)));
  });
  return `#${ch.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/** 티어별 갑옷 색. 민병은 칙칙하고 정예는 번쩍인다. */
function tierPalette(p: Palette, tier: number): Palette {
  if (tier <= 1) return { ...p, metal: shade(p.wood, 0.1), body: shade(p.body, 0.18), trim: p.wood };
  if (tier === 2) return p;
  if (tier === 3) return { ...p, metal: shade(p.metal, 0.18), trim: shade(p.trim, 0.12) };
  return { ...p, metal: shade(p.metal, 0.3), trim: '#e8c96a' };
}

/**
 * 동료 초상. 전장의 동료 그림을 캔버스 하나에 그린다. 합류 장면이 크게 늘려 쓴다
 * (CSS `image-rendering: pixelated`). 한 칸이 `CELL` 픽셀이다.
 *
 * 색은 동료의 나라를 따른다. 처음에는 고구려 색 하나로 그려, 백제·신라 동료도
 * 고구려 옷을 입고 나왔다(리뷰가 찾았다). 동료마다 다른 얼굴은 아직 없다 — 전장의
 * 동료 그림이 하나라서, 초상도 그 그림을 크게 보여 주는 것이다.
 *
 * 걷는 몸으로 그린다. 막 합류한 동료는 장비가 없어 전장에 걸어서 나온다
 * (`dressCompanion`). 말 탄 모습으로 맞이하면 첫 출정에서 다른 사람이 나온다.
 */
export function drawCompanionPortrait(canvas: HTMLCanvasElement, culture: Culture): void {
  canvas.width = CELL;
  canvas.height = CELL;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.imageSmoothingEnabled = false;
  const palette = tierPalette(CULTURE_PALETTE[culture], 4);
  drawUnit(ctx, 0, 0, palette, BranchCode.Blade, 4, true, false, SQUAD_COLORS[1]);
}

/** 아틀라스를 굽는다. 브라우저에서만 호출된다. */
export function buildAtlas(): AtlasResult {
  const cells: ((ctx: CanvasRenderingContext2D, x: number, y: number) => void)[] = [];
  const keys: string[] = [];

  const add = (key: string, draw: (ctx: CanvasRenderingContext2D, x: number, y: number) => void): void => {
    keys.push(key);
    cells.push(draw);
  };

  // 병종 표에 실제로 있는 조합만 굽는다. 쓰지 않는 칸을 만들 이유가 없다.
  const seen = new Set<string>();
  for (const t of TROOPS) {
    const branch = BRANCH_CODE[t.branch];
    const key = troopSpriteKey(t.culture, branch, t.tier);
    if (seen.has(key)) continue;
    seen.add(key);
    const p = tierPalette(CULTURE_PALETTE[t.culture], t.tier);
    add(key, (ctx, ox, oy) => drawUnit(ctx, ox, oy, p, branch, t.tier, false));
  }

  const heroPalette = tierPalette(CULTURE_PALETTE[Culture.Goguryeo], 4);
  const chiefPalette = tierPalette(CULTURE_PALETTE[Culture.Silla], 4);
  add(HERO_LEADER_KEY, (ctx, ox, oy) => drawUnit(ctx, ox, oy, heroPalette, BranchCode.Horse, 4, true));
  add(HERO_LEADER_FOOT_KEY, (ctx, ox, oy) => drawUnit(ctx, ox, oy, heroPalette, BranchCode.Blade, 4, true));
  add(HERO_CHIEF_KEY, (ctx, ox, oy) =>
    drawUnit(ctx, ox, oy, chiefPalette, BranchCode.Horse, 4, true, true));
  add(HERO_CHIEF_FOOT_KEY, (ctx, ox, oy) =>
    drawUnit(ctx, ox, oy, chiefPalette, BranchCode.Blade, 4, true, true));

  // 동료는 장수와 같은 실루엣에 분대 색 투구 장식만 다르다.
  for (let sq = 1; sq < SQUAD_COLORS.length; sq++) {
    const color = SQUAD_COLORS[sq];
    add(companionKey(sq), (ctx, ox, oy) =>
      drawUnit(ctx, ox, oy, heroPalette, BranchCode.Horse, 4, true, false, color));
    add(companionFootKey(sq), (ctx, ox, oy) =>
      drawUnit(ctx, ox, oy, heroPalette, BranchCode.Blade, 4, true, false, color));
  }

  add('arrow', (ctx, ox, oy) => {
    rect(ctx, '#2b2016', ox + 10, oy + 13, 8, 1);
    rect(ctx, '#d8d2c0', ox + 18, oy + 13, 3, 1);
    rect(ctx, '#d8d2c0', ox + 17, oy + 12, 1, 1);
    rect(ctx, '#d8d2c0', ox + 17, oy + 14, 1, 1);
    rect(ctx, '#e8e2d0', ox + 8, oy + 12, 2, 3);
  });

  // 발밑 표식. 난전에서 피아 구분은 이 고리 하나로 결정된다.
  // 원작 화면에서 적 발밑에 붉은 원이 깔려 있는 것과 같은 장치다.
  const mark = (ring: string): ((ctx: CanvasRenderingContext2D, x: number, y: number) => void) =>
    (ctx, ox, oy) => {
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.beginPath();
      ctx.ellipse(ox + 14, oy + 15, 7, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = ring;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.ellipse(ox + 14, oy + 14, 7.5, 3.5, 0, 0, Math.PI * 2);
      ctx.stroke();
    };
  add('mark0', mark('rgba(126, 198, 232, 0.85)'));
  add('mark1', mark('rgba(226, 92, 72, 0.9)'));
  // 우리 용사(`ELITE`): 금빛 고리를 두 겹으로. 난전에서 "저 병사가 내 용사"가 발밑으로 읽혀야 한다 — 몸 그림은 같은 병종과 같다.
  add('mark2', (ctx, ox, oy) => {
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.beginPath();
    ctx.ellipse(ox + 14, oy + 15, 7, 3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 210, 90, 0.95)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.ellipse(ox + 14, oy + 14, 7.5, 3.5, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(ox + 14, oy + 14, 5, 2, 0, 0, Math.PI * 2);
    ctx.stroke();
  });

  // 적의 용사: 붉은 고리 안에 금빛 고리. 우리 용사(`mark2`)와 같은 꼴에 편의 색만 다르다 — "저 적이 용사다, 베면 크다".
  add('mark3', (ctx, ox, oy) => {
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.beginPath();
    ctx.ellipse(ox + 14, oy + 15, 7, 3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(226, 92, 72, 0.95)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.ellipse(ox + 14, oy + 14, 7.5, 3.5, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255, 210, 90, 0.95)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(ox + 14, oy + 14, 5, 2, 0, 0, Math.PI * 2);
    ctx.stroke();
  });

  add('blood', (ctx, ox, oy) => {
    rect(ctx, '#7d1f1f', ox + 11, oy + 12, 4, 2);
    rect(ctx, '#9c2a2a', ox + 13, oy + 14, 3, 2);
    rect(ctx, '#7d1f1f', ox + 16, oy + 13, 2, 1);
  });

  // 방패(`SHIELD_BY_TIER`). 병사 그림과 따로 그린다 — 부서지면 방패만 사라져야 한다(플레이한 사람: "방패를 스프라이트에
  // 합쳐그리면 안되고 따로 그려야 한다"). 둥근 나무 방패에 가운데 띠와 쇠 장식. 띠 색이 피아를 가른다(발밑 고리와 같은 뜻).
  const shield = (band: string): ((ctx: CanvasRenderingContext2D, x: number, y: number) => void) =>
    (ctx, ox, oy) => {
      rect(ctx, '#2a2014', ox + 12, oy + 9, 5, 1);
      rect(ctx, '#2a2014', ox + 11, oy + 10, 7, 9);
      rect(ctx, '#2a2014', ox + 12, oy + 19, 5, 1);
      rect(ctx, '#8a6236', ox + 12, oy + 10, 5, 9);
      rect(ctx, '#a67a44', ox + 12, oy + 10, 1, 8);
      rect(ctx, band, ox + 14, oy + 10, 1, 9);
      rect(ctx, '#e0d8c4', ox + 14, oy + 14, 1, 1);
    };
  add('shield0', shield('#4a8fc0'));
  add('shield1', shield('#c8482e'));

  add('spark', (ctx, ox, oy) => {
    rect(ctx, '#fff4c4', ox + 13, oy + 10, 3, 1);
    rect(ctx, '#fff4c4', ox + 14, oy + 9, 1, 3);
    rect(ctx, '#ffd98a', ox + 12, oy + 12, 2, 1);
  });

  for (let sq = 0; sq < SQUAD_COLORS.length; sq++) {
    const color = SQUAD_COLORS[sq];
    add(bannerKey(sq), (ctx, ox, oy) => {
      rect(ctx, '#3a2a18', ox + 13, oy + 3, 2, 22);
      rect(ctx, '#6b4a28', ox + 13, oy + 4, 1, 20);
      rect(ctx, '#1a1a16', ox + 14, oy + 4, 11, 10);
      rect(ctx, color, ox + 15, oy + 5, 9, 8);
      rect(ctx, '#1a1a16', ox + 17, oy + 7, 5, 1);
      rect(ctx, '#1a1a16', ox + 17, oy + 10, 5, 1);
      // 분대 번호를 막대 수로 표시한다. 색약이어도 세면 구분된다.
      for (let k = 0; k <= sq; k++) rect(ctx, color, ox + 11, oy + 16 + k * 3, 3, 2);
    });
  }

  const rows = Math.ceil(keys.length / COLS);
  const canvas = document.createElement('canvas');
  canvas.width = COLS * CELL;
  canvas.height = rows * CELL;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D 컨텍스트를 만들 수 없습니다');
  ctx.imageSmoothingEnabled = false;

  for (let i = 0; i < cells.length; i++) {
    const cx = (i % COLS) * CELL;
    const cy = Math.floor(i / COLS) * CELL;
    cells[i](ctx, cx, cy);
  }

  const base = Texture.from(canvas);
  base.source.scaleMode = 'nearest';

  const frames = new Map<string, Texture>();
  for (let i = 0; i < keys.length; i++) {
    const cx = (i % COLS) * CELL;
    const cy = Math.floor(i / COLS) * CELL;
    frames.set(
      keys[i],
      new Texture({ source: base.source, frame: new Rectangle(cx, cy, CELL, CELL) }),
    );
  }

  return { texture: base, frames, canvas };
}

function drawUnit(
  ctx: CanvasRenderingContext2D,
  ox: number,
  oy: number,
  p: Palette,
  branch: number,
  tier: number,
  hero: boolean,
  enemyHero = false,
  plumeOverride?: string,
): void {
  const mounted = branch === BranchCode.Horse;

  if (mounted) {
    put(ctx, HORSE, horseMap(p), ox + 4, oy + 8);
    // 4티어 기병은 말에도 철갑을 두른다. 개마무사라는 이름의 근거다.
    if (tier >= 4) {
      rect(ctx, p.metal, ox + 5, oy + 13, 11, 1);
      rect(ctx, p.dark, ox + 5, oy + 14, 11, 1);
    }
    put(ctx, FOOT, footMap(p, hero), ox + 7, oy + 1);
  } else {
    put(ctx, FOOT, footMap(p, false), ox + 8, oy + 6);
  }

  const bx = mounted ? ox + 7 : ox + 8;
  const by = mounted ? oy + 1 : oy + 6;

  if (hero) {
    // 장수는 투구 장식과 망토로 한눈에 구분된다.
    const plume = plumeOverride ?? (enemyHero ? '#c2402f' : '#e8c96a');
    rect(ctx, plume, bx + 5, by - 4, 2, 5);
    rect(ctx, plume, bx + 4, by - 1, 4, 1);
    rect(ctx, enemyHero ? '#2d2a44' : plumeOverride ? '#3b3f52' : '#8d2f28', bx + 1, by + 7, 10, 6);
    rect(ctx, '#dfe4e8', bx + 12, by + 2, 1, 11);
    rect(ctx, '#e8c96a', bx + 11, by + 13, 3, 1);
    return;
  }

  switch (branch) {
    case BranchCode.Spear: {
      // 창은 길게 위로 뻗는다. 멀리서도 병종이 구분돼야 한다.
      const extra = tier >= 4 ? 4 : 0;
      rect(ctx, p.wood, bx + 12, by - 4 - extra, 1, 18 + extra);
      rect(ctx, '#dfe4e8', bx + 12, by - 7 - extra, 1, 3);
      rect(ctx, '#dfe4e8', bx + 11, by - 5 - extra, 3, 1);
      break;
    }
    case BranchCode.Levy: {
      rect(ctx, p.wood, bx + 12, by + 1, 1, 12);
      rect(ctx, '#b9b2a4', bx + 12, by - 1, 1, 2);
      break;
    }
    case BranchCode.Blade: {
      rect(ctx, '#dfe4e8', bx + 12, by + 4, 1, 7 + (tier >= 3 ? 2 : 0));
      rect(ctx, p.dark, bx + 11, by + 11 + (tier >= 3 ? 2 : 0), 3, 1);
      break;
    }
    case BranchCode.Bow: {
      rect(ctx, p.wood, bx - 2, by + 5, 1, 7);
      rect(ctx, p.wood, bx - 1, by + 4, 1, 1);
      rect(ctx, p.wood, bx - 1, by + 12, 1, 1);
      rect(ctx, '#cfcfcf', bx, by + 5, 1, 7);
      // 4티어는 쇠뇌나 명궁이다. 활대를 두껍게 그려 구분한다.
      if (tier >= 4) rect(ctx, p.wood, bx - 3, by + 6, 1, 5);
      break;
    }
    case BranchCode.Horse: {
      rect(ctx, '#dfe4e8', bx + 12, by + 3, 1, 9);
      rect(ctx, tier >= 4 ? '#e8c96a' : p.dark, bx + 11, by + 12, 3, 1);
      break;
    }
    default:
      break;
  }
}

/** 바닥 타일. 흙과 마른 풀. */
export function buildGroundTexture(): Texture {
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D 컨텍스트를 만들 수 없습니다');
  ctx.fillStyle = '#6f6a4e';
  ctx.fillRect(0, 0, size, size);
  // 결정론과 무관한 장식이므로 여기서는 Math.random을 써도 된다.
  for (let i = 0; i < 90; i++) {
    const x = Math.floor(Math.random() * size);
    const y = Math.floor(Math.random() * size);
    const shade = ['#7a755a', '#645f46', '#837c5e', '#5b563f'][Math.floor(Math.random() * 4)];
    ctx.fillStyle = shade;
    ctx.fillRect(x, y, 1 + Math.floor(Math.random() * 2), 1);
  }
  const tex = Texture.from(canvas);
  tex.source.scaleMode = 'nearest';
  return tex;
}

/**
 * 위아래 열린 가장자리에 까는 옅어짐. 땅이 화면 밖으로 이어지다 흐려진다.
 *
 * 벽 대신 이것을 둔다. 양군이 들어온 쪽이고 후퇴하면 빠져나가는 쪽이다. 벽처럼
 * 그리면 플레이어가 거기로 물러날 수 있다는 것을 모른다. 1px 폭의 세로 그라데이션을
 * 가로로 늘려 쓴다. `fadeTo`가 위에 있으면 위로 갈수록 짙어진다.
 */
export function buildEdgeFadeTexture(height: number, fadeTo: 'top' | 'bottom'): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D 컨텍스트를 만들 수 없습니다');
  const g = ctx.createLinearGradient(0, 0, 0, height);
  // 렌더러 배경색과 같다. 땅이 배경 속으로 녹아든다.
  const solid = 'rgba(43, 42, 36, 1)';
  const clear = 'rgba(43, 42, 36, 0)';
  g.addColorStop(0, fadeTo === 'top' ? solid : clear);
  g.addColorStop(1, fadeTo === 'top' ? clear : solid);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 1, height);
  return Texture.from(canvas);
}

/** 전장 좌우 테두리. 바위를 늘어놓으면 덤불이 떠 있는 것처럼 보인다. */
export function buildWallTexture(): Texture {
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D 컨텍스트를 만들 수 없습니다');
  ctx.fillStyle = '#26251f';
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = '#332f27';
  for (let y = 0; y < size; y += 8) {
    for (let x = (y / 8) % 2 === 0 ? 0 : 8; x < size; x += 16) {
      ctx.fillRect(x + 1, y + 1, 14, 6);
    }
  }
  const tex = Texture.from(canvas);
  tex.source.scaleMode = 'nearest';
  return tex;
}

/** 막힌 타일에 얹는 바위. */
export function buildRockTexture(): Texture {
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D 컨텍스트를 만들 수 없습니다');
  // 마른 덤불과 바위 무더기. 네모로 두면 상자처럼 보인다.
  ctx.fillStyle = '#3b3a33';
  ctx.fillRect(6, 20, 20, 6);
  ctx.fillRect(4, 22, 24, 3);
  ctx.fillStyle = '#56564d';
  ctx.fillRect(8, 12, 12, 10);
  ctx.fillRect(6, 15, 16, 6);
  ctx.fillRect(18, 16, 8, 7);
  ctx.fillStyle = '#6a6a5e';
  ctx.fillRect(10, 13, 7, 5);
  ctx.fillRect(20, 17, 4, 3);
  ctx.fillStyle = '#2b2a24';
  ctx.fillRect(9, 25, 14, 2);
  ctx.fillStyle = '#4e5a3c';
  ctx.fillRect(3, 17, 3, 5);
  ctx.fillRect(25, 13, 3, 6);
  ctx.fillRect(14, 9, 3, 4);
  const tex = Texture.from(canvas);
  tex.source.scaleMode = 'nearest';
  return tex;
}

/**
 * 지형 타일 셋.
 *
 * 색으로만 구분하면 색약인 사람에게는 같은 바닥이다. 숲은 나무 꼴, 늪은
 * 웅덩이와 갈대, 언덕은 등고선으로 **모양을 다르게** 그린다. 셋 다 지나갈 수
 * 있는 땅이므로 바위처럼 꽉 채우지 않고 바닥이 비치게 둔다.
 */
function tile(draw: (ctx: CanvasRenderingContext2D) => void): Texture {
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D 컨텍스트를 만들 수 없습니다');
  draw(ctx);
  const tex = Texture.from(canvas);
  tex.source.scaleMode = 'nearest';
  return tex;
}

export function buildStakesTexture(): Texture {
  return tile((ctx) => {
    // 거마(拒馬): 끝을 깎은 말뚝을 엇갈려 묶은 틀 두 개. 바닥이 비쳐야 지나갈 수 있는 땅으로 읽힌다 — 목책처럼 꽉 채우면
    // 바위로 보였다. 그림자를 아래에 깔아 서 있는 것으로.
    const cross = (x: number, y: number) => {
      ctx.fillStyle = 'rgba(30, 22, 14, 0.45)';
      ctx.fillRect(x - 1, y + 9, 13, 2);
      for (let k = 0; k < 9; k++) {
        // 두 말뚝이 X로 엇갈린다. 한 칸씩 찍어 사선을 긋는다.
        ctx.fillStyle = '#6f5030';
        ctx.fillRect(x + k, y + k, 2, 2);
        ctx.fillRect(x + 9 - k, y + k, 2, 2);
      }
      ctx.fillStyle = '#b8935f';
      ctx.fillRect(x, y, 1, 1);
      ctx.fillRect(x + 10, y, 1, 1);
      ctx.fillStyle = '#4a3420';
      ctx.fillRect(x + 1, y + 5, 9, 1);
    };
    cross(3, 6);
    cross(18, 14);
  });
}

export function buildForestTexture(): Texture {
  return tile((ctx) => {
    // 침엽수 세 그루. 밑동을 어둡게 깔아 바닥과 분리한다.
    const tree = (x: number, y: number, h: number) => {
      ctx.fillStyle = '#2e2419';
      ctx.fillRect(x + 2, y + h, 2, 4);
      ctx.fillStyle = '#2f4a2a';
      ctx.fillRect(x, y + h - 4, 6, 5);
      ctx.fillRect(x + 1, y + h - 8, 4, 5);
      ctx.fillStyle = '#3f6437';
      ctx.fillRect(x + 1, y + h - 7, 3, 3);
      ctx.fillRect(x + 2, y + h - 3, 3, 3);
    };
    tree(4, 6, 12);
    tree(18, 2, 14);
    tree(12, 16, 10);
  });
}

export function buildMarshTexture(): Texture {
  return tile((ctx) => {
    // 고인 물과 갈대. 물빛을 둘로 나눠 얕고 깊은 데가 보이게 한다.
    ctx.fillStyle = '#3a4038';
    ctx.fillRect(2, 8, 28, 16);
    ctx.fillStyle = '#44584f';
    ctx.fillRect(5, 11, 12, 8);
    ctx.fillRect(18, 15, 9, 6);
    ctx.fillStyle = '#5a7268';
    ctx.fillRect(7, 13, 6, 3);
    ctx.fillRect(20, 16, 4, 2);
    ctx.fillStyle = '#6b7a4a';
    for (const [x, y, h] of [
      [4, 6, 6],
      [10, 4, 7],
      [24, 7, 6],
      [28, 12, 5],
      [15, 22, 5],
    ]) {
      ctx.fillRect(x, y, 1, h);
      ctx.fillRect(x + 1, y + 2, 1, h - 2);
    }
  });
}

export function buildFordTexture(): Texture {
  return tile((ctx) => {
    // 얕은 물. 칸을 꽉 채워 이웃 칸과 이어져야 한 줄기 물로 읽힌다 — 늪처럼 가운데만 칠하면 웅덩이가 늘어선다.
    // 물결 두 줄과 비치는 자갈로 얕음을 보인다(깊은 물빛이면 못 건너는 강으로 읽힌다).
    ctx.fillStyle = '#4d6b6c';
    ctx.fillRect(0, 0, 32, 32);
    ctx.fillStyle = '#58797a';
    ctx.fillRect(0, 4, 32, 9);
    ctx.fillRect(0, 19, 32, 8);
    ctx.fillStyle = '#8fb1ad';
    for (const [x, y, w] of [
      [3, 7, 6],
      [16, 9, 7],
      [9, 21, 5],
      [23, 23, 6],
    ]) {
      ctx.fillRect(x, y, w, 1);
      ctx.fillRect(x + 1, y - 1, w - 2, 1);
    }
    ctx.fillStyle = '#6f7a66';
    for (const [x, y] of [
      [12, 15],
      [26, 14],
      [5, 27],
      [19, 29],
    ]) {
      ctx.fillRect(x, y, 2, 1);
    }
  });
}

export function buildHillTexture(): Texture {
  return tile((ctx) => {
    // 바닥색과 가까운 흙빛으로 평평하게 칠하면 상자로 보인다. 처음 그린 것이
    // 그랬다. 높이가 읽히려면 **윤곽이 둥글고 한쪽이 밝아야** 한다.
    // 왼쪽 위에서 빛이 온다고 보고 그 면을 밝게, 반대쪽에 그림자를 깐다.
    const mound = (
      cx: number,
      cy: number,
      w: number,
      h: number,
      dark: string,
      mid: string,
      lit: string,
    ) => {
      ctx.fillStyle = dark;
      ctx.fillRect(cx - w, cy - 1, w * 2, h + 2);
      ctx.fillRect(cx - w + 2, cy - 3, w * 2 - 4, h + 4);
      ctx.fillStyle = mid;
      ctx.fillRect(cx - w + 1, cy - 2, w * 2 - 3, h + 1);
      ctx.fillRect(cx - w + 3, cy - 4, w * 2 - 7, h + 3);
      ctx.fillStyle = lit;
      ctx.fillRect(cx - w + 2, cy - 3, w - 1, h - 1);
      ctx.fillRect(cx - w + 4, cy - 5, w - 3, h);
    };
    // 뒤쪽 낮은 둔덕, 앞쪽 높은 둔덕. 겹쳐야 하나의 능선으로 보인다.
    mound(20, 12, 9, 6, '#4a4a31', '#63643f', '#77794d');
    mound(12, 20, 11, 7, '#3f4029', '#585a38', '#6e7045');
    // 능선 위 풀 몇 포기. 흙만 있으면 모래언덕처럼 보인다.
    ctx.fillStyle = '#7f8a4e';
    for (const [x, y] of [
      [6, 16],
      [15, 13],
      [23, 9],
      [10, 22],
      [19, 19],
    ]) {
      ctx.fillRect(x, y, 1, 2);
      ctx.fillRect(x + 1, y + 1, 1, 2);
    }
  });
}
